import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import requestDigitizedAssetOperation from 'editor/src/store/design/operation/requestDigitizedAssetOperation';
import getDigitizedAsset from 'editor/src/store/design/selector/getDigitizedAsset';
import getDigitizedAssetByUuid from 'editor/src/store/design/selector/getDigitizedAssetByUuid';
import { DigitizedAssetFromType, MediaAddon, MediaImage, MediaText } from 'editor/src/store/design/types';
import getDigitizedElementId from 'editor/src/store/design/util/getDigitizedElementId';
import getReusableDigitizedImageId from 'editor/src/store/design/util/getReusableDigitizedImageId';
import { useDispatch, useSelector } from 'editor/src/store/hooks';
import shouldDigitizeCurrentSpread from 'editor/src/store/utils/shouldDigitizeCurrentSpread';

import { RequestDigitizedAssetDataFromType } from 'editor/src/util/postMessages/messages';

function useIsProtectedFont(element: MediaText | MediaImage | MediaAddon): boolean {
  const uploadedFonts = useSelector((state) => state.fonts.uploadedFonts);
  if (element.type !== 'text') {
    return false;
  }
  const fontFile = (element as MediaText).extra.fontFamily;
  const uploadedFont = uploadedFonts.find((f) => f.fontFile === fontFile);
  return uploadedFont?.isProtected === true;
}

const DEBOUNCE_TIME = 1000;

function getElementImageId(element: MediaText | MediaImage | MediaAddon): string | undefined {
  if (element.type === 'image') {
    return element.imageId;
  }
  if (element.type === 'addon') {
    return element.addonId;
  }
  return undefined;
}

function useDigitizedAsset<
  T extends MediaText | MediaImage | MediaAddon,
  R extends RequestDigitizedAssetDataFromType<T['type']>,
  D extends DigitizedAssetFromType<T['type']>,
>(
  element: T,
  disable: boolean,
  getRequestPayload: (
    imageId: string | undefined,
    productUid: string,
    variantKey: string | undefined,
    elementId: string,
  ) => R | undefined,
) {
  const dispatch = useDispatch();

  const isEmbroiderySpread = useSelector(shouldDigitizeCurrentSpread);
  const isProtectedFont = useIsProtectedFont(element);
  const digitizeDesignElements = isEmbroiderySpread || isProtectedFont;
  const variantKey = useSelector((state) => state.variants.selectedGroupKey);
  const elementId = useMemo(
    () => (digitizeDesignElements ? getDigitizedElementId(element) : undefined),
    [digitizeDesignElements, element],
  );
  const digitizedAsset = useSelector((state) => getDigitizedAsset<D>(state, element));
  const digitizedAssetByUuid = useSelector((state) => getDigitizedAssetByUuid<D>(state, element.uuid));
  const productUid = useSelector((state) => state.design.designData?.product_uid);
  const requestPayloadRef = useRef(getRequestPayload);
  requestPayloadRef.current = getRequestPayload;

  const debounceRequest = useCallback(
    debounce(
      (request: R) => {
        dispatch(requestDigitizedAssetOperation(request));
      },
      DEBOUNCE_TIME,
      { leading: true, trailing: true },
    ),
    [element.uuid],
  );

  useEffect(() => () => debounceRequest.cancel(), [debounceRequest]);

  useEffect(() => {
    if (disable || !digitizeDesignElements || !productUid || !elementId) {
      // a trailing call queued for the previous content would digitize what is no longer there
      debounceRequest.cancel();
      return;
    }

    const originalImageId = getElementImageId(element);

    try {
      const requestRef = requestPayloadRef.current(originalImageId, productUid, variantKey, elementId);
      if (requestRef) {
        const stitchFileId = getReusableDigitizedImageId(digitizedAsset || digitizedAssetByUuid, requestRef.payload);
        // Without a stitch file to re-render, the host has to digitize the image, as for a newly placed one.
        debounceRequest(
          stitchFileId ? { ...requestRef, imageId: stitchFileId } : { ...requestRef, operationType: 'create' },
        );
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Digitize] Failed to build request payload:', err);
    }
  }, [
    digitizeDesignElements,
    elementId,
    disable,
    productUid,
    variantKey,
    getElementImageId(element),
    digitizedAsset?.imageId,
    digitizedAssetByUuid?.imageId,
  ]);

  return {
    digitizedAsset,
    showDigitizedAsset: !disable && digitizeDesignElements,
  };
}

export default useDigitizedAsset;
